import React, { forwardRef } from 'react';

import { useDraggable } from '@dnd-kit/core';
import type { ButtonBaseProps } from '@mui/material';
import { ButtonBase, Typography } from '@mui/material';
import clsx from 'clsx';
import { GripVertical } from 'lucide-react';
import type { DragField } from 'src/components/drag-and-drop';
import { AlineTooltip } from 'src/components/generic';
import { composeRefs } from 'src/helpers/compose-refs';
import { makeStyles } from 'tss-react/mui';

const DRAG_ICON_WIDTH = 10;

const useStyles = makeStyles<StyleProps, 'dragIcon' | 'text'>()((theme, { disabled }, classes) => ({
  button: {
    '&:hover': {
      border: disabled
        ? `1px solid ${theme.appColors.rule}`
        : `1px solid ${theme.appColors.primary}`,
    },
    [`&:hover .${classes.dragIcon}`]: {
      opacity: 1,
    },
    // Make room for the handle only while it shows, so the label isn't cut short at rest.
    [`&:hover .${classes.text}`]: {
      marginRight: `calc(${DRAG_ICON_WIDTH}px + ${theme.spacing(0.5)})`,
    },
    backgroundColor: theme.appColors.background,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 3,
    boxSizing: 'border-box',
    color: disabled ? theme.palette.action.disabled : theme.appColors.iconsType,
    // Explicitly set the height so the button doesn't "jump" on hover.
    height: 34,
    minWidth: 0,
    padding: theme.spacing(0.5, 1),
    textDecoration: 'none',
    width: '100%',
  },
  /**
   * Positioned outside the flex row so the hidden handle takes no width from the label. Hidden
   * with opacity, not visibility, so Playwright can still hover the handle to drag it.
   */
  dragIcon: {
    color: theme.appColors.iconsTypeMuted,
    cursor: 'grab',
    opacity: 0,
    position: 'absolute',
    right: theme.spacing(1),
    top: '50%',
    transform: 'translateY(-50%)',
  },
  text: {
    flexGrow: 1,
    marginLeft: theme.spacing(1),
    minWidth: 0,
  },
}));

type StyleProps = { disabled: boolean };

interface InputFieldButtonProps extends Omit<ButtonBaseProps, 'disabled'> {
  dataCy: string;
  dragField: DragField;
  LeftIcon: React.FC<{ disabled?: boolean }>;
  text: string;
  disabledReason?: string;
  dragDataCy?: string;
  helpText?: string;
}

export const InputFieldButton = forwardRef(function InputFieldButton(
  props: InputFieldButtonProps,
  ref: React.Ref<HTMLButtonElement>
): JSX.Element {
  const {
    LeftIcon,
    className,
    dataCy,
    disabledReason = '',
    dragDataCy,
    dragField,
    helpText = '',
    text,
    ...rest
  } = props;
  const isDisabled = Boolean(disabledReason);
  const { classes } = useStyles({ disabled: isDisabled });
  const { attributes, listeners, setNodeRef } = useDraggable({
    data: dragField,
    id: dragField.nodeId,
  });
  const tooltipTitle = disabledReason || helpText;

  return (
    <AlineTooltip arrow placement="top" title={tooltipTitle}>
      <div style={{ minWidth: 0, width: '100%' }}>
        <ButtonBase
          ref={composeRefs<HTMLButtonElement>(ref, setNodeRef)}
          className={clsx(classes.button, className)}
          data-cy={dataCy}
          disabled={isDisabled}
          {...attributes}
          {...listeners}
          {...rest}
        >
          <LeftIcon disabled={isDisabled} />
          <Typography noWrap align="left" className={classes.text} color="inherit" variant="body1">
            {text}
          </Typography>
          {/* Crops the empty sides of the 24-unit box so the grip sits nearer the card's right edge. */}
          <GripVertical
            className={classes.dragIcon}
            data-cy={dragDataCy}
            height={20}
            viewBox="6 0 12 24"
            width={DRAG_ICON_WIDTH}
          />
        </ButtonBase>
      </div>
    </AlineTooltip>
  );
});
